import { useState, useEffect, useRef } from 'react';

const useCustomSpeechRecognition = () => {
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [isChromium, setIsChromium] = useState(false);
  const recognition = useRef(null);

  useEffect(() => {
    const isSafariCheck =
      /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
    const isChromiumCheck =
      /Chrome/.test(navigator.userAgent) &&
      /Google Inc/.test(navigator.vendor);
    setIsSafari(isSafariCheck);
    setIsChromium(isChromiumCheck);

    const initSpeechRecognition = () => {
      window.SpeechRecognition =
        window.SpeechRecognition ||
        window.webkitSpeechRecognition;
      if (!window.SpeechRecognition) {
        console.error(
          'Speech recognition is not supported on this browser'
        );
        return;
      }

      recognition.current = new window.SpeechRecognition();
      recognition.current.continuous = true;
      recognition.current.interimResults = true;
      recognition.current.onresult = (event) => {
        const currentTranscript = Array.from(event.results)
          .map((result) => result[0].transcript)
          .join('');
        setTranscript(currentTranscript);
      };
      recognition.current.onend = () => {
        setIsListening(false);
      };

      const handleBeforeUnload = () => {
        recognition.current.stop();
      };
      window.addEventListener(
        'beforeunload',
        handleBeforeUnload
      );

      return () => {
        recognition.current.stop();
        window.removeEventListener(
          'beforeunload',
          handleBeforeUnload
        );
        setTranscript('');
      };
    };

    if (!isChromium && !isSafari) {
      return;
    }

    if (isSafari) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then(() => {
          initSpeechRecognition();
        })
        .catch((error) => {
          console.error(
            'Error accessing microphone:',
            error
          );
        });
    } else {
      initSpeechRecognition();
    }
  }, []);

  const startListening = () => {
    if (!isChromium && !isSafari) {
      // console.warn(
      //   'Speech recognition is only supported in Chromium-based browsers and Safari'
      // );
      return;
    }
    recognition.current.lang = 'fa-IR';
    recognition.current.start();
    setIsListening(true);
  };

  const stopListening = () => {
    if (!isChromium && !isSafari) {
      // console.warn(
      //   'Speech recognition is only supported in Chromium-based browsers and Safari'
      // );
      return;
    }
    setIsListening(false);
    recognition.current.stop();
    setTranscript('');
  };

  return {
    transcript,
    isListening,
    startListening,
    stopListening,
    setTranscript,
    isSupportedBrowser: isChromium || isSafari,
  };
};

export default useCustomSpeechRecognition;
